import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '@/lib/api';
import { isRealDate } from '@/components/catalog/form-utils';
import { sumDecimalStrings } from '@/lib/decimal';
import { formatDecimalString, pluralise } from '@/lib/format';
import { todayLocal } from '@/lib/document-naming';
import { QUOTE_LINES_ALREADY_INVOICED_CODE, addDaysIso } from '@/lib/quote-utils';
/*
 * ⚠️ The Xero outcome is classified and worded in `@/lib/xero`, and rendered by
 * the banner the list rows already use. Nothing here words a Xero refusal of its
 * own: one server code described differently on a list row, a detail page and this
 * dialog is the exact drift that module was consolidated to stop.
 */
import { conversionPushReport, type XeroRowPushReport } from '@/lib/xero';
import { XeroPushReportBanner } from '@/components/xero/XeroRowPushControl';
import type { Invoice, Quote, QuoteProduct, QuotePartialInvoiceInput } from '@/lib/types';
import {
  Button,
  Checkbox,
  Field,
  Input,
  Modal,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';

/*
 * ── BILLING A QUOTE A LINE AT A TIME ──────────────────────────────────────────
 *
 * `POST /quotes/{id}/invoices` raises an invoice for the lines somebody picks,
 * where `convert` bills the whole document at once. This is the only surface in
 * the app that calls it.
 *
 * ⚠️ **It is NOT gated on `billingMode`, and it must not be.** The handler never
 * selects the column: a partial raise works on a `full` quote, on an
 * already-converted quote, and in any stage. `billingMode: 'staged'` decides
 * exactly one thing — whether reaching the accepting stage auto-raises an
 * invoice — and nothing else. Offering this control only on staged quotes would
 * be this client enforcing a rule the server does not have, which is the single
 * most likely mistake here. A staged quote is the case that has NO other billing
 * route, not the only case this applies to.
 *
 * ⚠️ **It does NOT move the quote's stage.** `Accepted` is what the acceptance
 * hook watches, so a partial bill deliberately leaves the quote where it is. The
 * copy below says so, because a person who has just billed the whole basket this
 * way will otherwise expect the badge to change and read its not changing as a
 * failure.
 *
 * ⚠️ **ONE refusal now carries a code, and the rest still do not** — corrected
 * 2026-08-29, when the backend added `QUOTE_LINES_ALREADY_INVOICED`
 * (`da76a3f`). This comment previously said flatly that there was no
 * `error.code` to route on, which was true when written.
 *
 * Everything else is still bare `NOT_FOUND` / `UNPROCESSABLE_ENTITY` /
 * `CONFLICT`, so `refusalOf()` keeps routing on the STATUS and `details[].path`
 * and keeps rendering the server's own sentences — the only text naming which
 * line items were refused. The code is read on top of that, never instead of
 * it, because it changes only WHERE the reader is sent and the server's
 * sentences carry everything else.
 */

/** The three optional keys this form renders a control for. */
const FORM_KEYS = ['subject', 'billDate', 'dueDate'] as const;

/** The server's own ceiling on one call. */
const MAX_LINES = 200;

/** The server's own ceiling on the subject. */
const MAX_SUBJECT = 255;

/**
 * The payment term the due-date prefill suggests, in days.
 *
 * ⚠️ **A SECOND COPY of the backend's `INVOICE_PAYMENT_TERM_DAYS`, and nothing
 * keeps the two in step.** That is tolerable here and would not be anywhere
 * else, for one reason: this only ever fills a box a person can see and edit
 * before saving. Clearing the box omits the key, and the server applies its own
 * figure — so if the term ever changes, the cost is a suggested date somebody
 * corrects on screen, never a wrong date stored silently. Do not reach for this
 * constant anywhere it would decide a stored value.
 */
const PAYMENT_TERM_DAYS = 30;

/** `quoteProductIds.4` — an array-element refusal, indexed into what we SENT. */
const INDEXED_PATH = /^quoteProductIds\.(\d+)$/;

interface Refusal {
  /** Bound to one of `FORM_KEYS`, safe to mark on its control. */
  fields: Record<string, string>;
  /** Keyed by `f_quote_products.id`, from an indexed path we could map. */
  lines: Record<number, string>;
  /** Everything else. Rendered at form level, in the order it arrived. */
  banner: string[];
}

const EMPTY_REFUSAL: Refusal = { fields: {}, lines: {}, banner: [] };

/**
 * Turn a refusal into something the form can render, with **no code to switch
 * on**.
 *
 * ## Two message shapes, and `error.message` is only sometimes the useful one
 *
 * A **zod** refusal answers `message: "Validation failed"` and carries the real
 * sentence in `details[0].message`. The two **service** 422s — *"Line item(s) 4,
 * 9 are not on quote 27"* and *"2 of the requested line item(s) are already
 * invoiced. Still uninvoiced on this quote: 11, 12."* — carry it in **both**. A
 * 403 or a 404 carries no `details` at all.
 *
 * So the details are read first and the top-level message is added only when it
 * says something they did not. It is **not** `details ?? message`: those are two
 * things that can both be true, and coalescing drops whichever side loses. The
 * containment test is what stops the service shape printing its one sentence
 * twice.
 *
 * ## Where each message goes
 *
 * `routeFieldErrors` already partitions on a form's own write-key list, so the
 * only thing done by hand here is the **indexed path**: `quoteProductIds.4`
 * names the fifth id in the array WE sent, so it can be mapped back to a real
 * line and marked on its row. Those paths are handed to `routeFieldErrors` as
 * known keys and moved out of `fields` afterwards, rather than routed
 * separately — that way the "a path that binds to nothing goes to the banner"
 * rule stays in one place.
 *
 * An index we cannot map is deliberately left unroutable rather than guessed at.
 */
function refusalOf(error: unknown, sentIds: number[]): Refusal {
  if (!(error instanceof ApiError)) {
    /*
     * No answer at all. ⚠️ Nothing may claim the invoice was not raised — the
     * request may well have landed — so this states the uncertainty and points
     * at the panel that can settle it.
     */
    return {
      fields: {},
      lines: {},
      banner: [
        'The request didn’t complete. Check “Invoices raised from this quote” before trying again.',
      ],
    };
  }

  const lineIdByPath = new Map<string, number>();
  for (const detail of error.details ?? []) {
    const match = INDEXED_PATH.exec(detail.path);
    if (match === null) continue;
    const index = Number(match[1]);
    const lineId = sentIds[index];
    if (lineId === undefined) continue;
    lineIdByPath.set(detail.path, lineId);
  }

  const routed = error.routeFieldErrors([...FORM_KEYS, ...lineIdByPath.keys()]);

  const fields: Record<string, string> = {};
  const lines: Record<number, string> = {};
  for (const [path, message] of Object.entries(routed.fields)) {
    const lineId = lineIdByPath.get(path);
    if (lineId === undefined) {
      fields[path] = message;
    } else {
      lines[lineId] = message;
    }
  }

  const banner = [...routed.unroutable];
  const top = error.message.trim();
  const generic = top === '' || top.toLowerCase() === 'validation failed';
  const alreadySaid =
    banner.some((line) => line.includes(top)) ||
    Object.values(fields).some((message) => message.includes(top)) ||
    Object.values(lines).some((message) => message.includes(top));
  if (!generic && !alreadySaid) banner.push(top);

  if (banner.length === 0 && Object.keys(fields).length === 0 && Object.keys(lines).length === 0) {
    banner.push(top === '' ? 'The invoice couldn’t be raised.' : top);
  }

  /*
   * The destination, and ONLY the destination.
   *
   * The server has already said which lines were taken, which invoice holds
   * each one, and what is still available — in `details` and in the top-level
   * message, both of which are above by now. Repeating any of that would be a
   * second copy of a sentence we do not own. What the code buys is the one
   * thing the prose does not state: that this refusal means the selection was
   * REASONABLE and the world moved, so the next move is the named invoice
   * rather than the chips on this form.
   *
   * ⚠️ Appended last, after the server's sentences, because it refers to the
   * invoice THEY name. Reordering these silently makes "that invoice"
   * antecedent-less.
   *
   * ⚠️ The invoice id is deliberately NOT parsed out of the message to build a
   * link. It is available only inside prose, and lifting an id out of a
   * sentence is matching on wording by another name — the thing this codebase
   * refuses everywhere else. The rows behind this form are re-read on every
   * refusal, so the billed state updates in place regardless.
   */
  if (error.code === QUOTE_LINES_ALREADY_INVOICED_CODE) {
    banner.push(
      'Nothing was raised — those lines were billed on the invoice named above.',
    );
  }

  return { fields, lines, banner };
}

export interface BillQuoteLinesModalProps {
  open: boolean;
  quote: Quote;
  onClose: () => void;
  /** The invoice the server created. Navigate to it — never feed it into quote state. */
  onRaised: (invoice: Invoice) => void;
}

export function BillQuoteLinesModal({ open, quote, onClose, onRaised }: BillQuoteLinesModalProps) {
  /*
   * ⚠️ **The lines are read HERE, when the dialog opens, and not taken from the
   * page.** `invoiced` / `invoicedOn` are derived on every read from
   * `f_invoice_products` and are never stored — deleting an invoice removes its
   * lines inside the database with no application code running, so a copy of
   * these flags drifts silently, and the drift shows a line as "already billed"
   * that nothing bills. The freshest possible reading is the one taken at the
   * moment somebody is about to choose.
   *
   * ⚠️ And `getQuote` carries no lines at all and neither field, so this call is
   * not optional — the products endpoint is the only place the answer exists.
   */
  const [lines, setLines] = useState<QuoteProduct[] | null>(null);
  const [linesError, setLinesError] = useState<string | null>(null);
  /* True from the first render: the read is fired by the effect below on mount. */
  const [linesLoading, setLinesLoading] = useState(true);
  /* Bumped to re-read — by "Try again", and by every refusal. */
  const [reloadToken, setReloadToken] = useState(0);

  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  /*
   * ⚠️ **PREFILLED WITH WHAT THE SERVER WOULD HAVE DEFAULTED TO ANYWAY, and
   * that is the whole point rather than a convenience.**
   *
   * These three were `''`, and every one of them already had a server-side
   * default: `input.subject ?? quote.subject`, `input.billDate ?? today()`,
   * and `input.dueDate ?? addDays(billDate, 30)` (`quote-convert.ts:563`,
   * `:568`, `:643`). So the invoice came out correctly filled — and the dialog
   * showed three empty boxes, which reads as *"these will be blank"*. The
   * person cannot confirm a value they cannot see, and cannot adjust a date
   * they have not been shown.
   *
   * ⚠️ **`billDate` is the one that genuinely changes**, and it is worth being
   * honest about the direction: the server's `today()` is the SERVER's day,
   * this is the VIEWER's. Around midnight they differ. The viewer's local date
   * is what a person means by "today" and is now what the box says, so the
   * screen and the document agree — which is the property to keep. Built from
   * local parts through `todayLocal`; `toISOString().slice(0, 10)` is UTC and
   * returns yesterday all evening west of Greenwich.
   *
   * ⚠️ **`INVOICE_PAYMENT_TERM_DAYS` is 30 in the BACKEND and 30 here, in two
   * places that nothing keeps in step.** This is a prefill a person can edit
   * before saving, not a second writer — clear the box and the server's own
   * default applies again, because `buildBody` still OMITS an empty key rather
   * than sending `''`. If the term ever changes, the visible cost is a
   * suggested date somebody corrects, never a silently wrong stored one.
   */
  const [subject, setSubject] = useState(() => quote.subject.slice(0, MAX_SUBJECT));
  const [billDate, setBillDate] = useState(() => todayLocal());
  const [dueDate, setDueDate] = useState(() => addDaysIso(todayLocal(), PAYMENT_TERM_DAYS));

  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<Refusal>(EMPTY_REFUSAL);
  /**
   * ── THE INVOICE WAS RAISED, AND XERO WOULD NOT TAKE IT ────────────────────
   *
   * Set only where {@link conversionPushReport} has something that must be read
   * BEFORE the person is taken to the new invoice — which is a `failed` push and
   * nothing else. `pushed` and `skipped` answer `null` there and this stays
   * `null`, so the ordinary raise is unchanged: `onRaised` fires immediately and
   * the caller navigates.
   *
   * ⚠️ **Non-null is what STOPS the navigation, and it has to.** `onRaised`
   * unmounts this dialog and moves the app to `/invoices/{id}`, so a band rendered
   * beside it would be destroyed before anybody read it. And the destination
   * cannot say it either: the server does not re-read the record on a failed push
   * because nothing was written, so the invoice arrives with no Xero link and its
   * own panel cannot tell that from an invoice nobody has pushed yet. **This
   * report is the only artefact anywhere that carries the fact.**
   *
   * ⚠️ **The invoice is held with it, and closing MUST still hand it over.** It is
   * a real document demanding real money; walking away from it silently is the
   * outcome that produces a second one, which is exactly why `quote_id` carrying
   * no UNIQUE constraint matters here.
   */
  const [raised, setRaised] = useState<{ invoice: Invoice; report: XeroRowPushReport } | null>(
    null,
  );

  const quoteId = quote.id;

  /*
   * ⚠️ **There is no reset-on-open effect, and there must not be one.** The
   * caller REMOUNTS this component when the dialog opens, keyed on that, so
   * every field above starts at its declared initial value — a selection, a
   * typed subject and above all the previous attempt's refusal cannot survive
   * into a second opening. Clearing them in an effect would do the same thing
   * one render later, which is a render in which the stale refusal is on screen.
   */

  /*
   * Ask for the basket again. `linesLoading` is raised HERE rather than inside
   * the effect, so the effect only ever synchronises with the network — the
   * skeleton belongs to the event that asked for the read.
   */
  const reload = useCallback(() => {
    setLinesLoading(true);
    setReloadToken((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    api
      .listQuoteProducts(quoteId, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setLines(response.data);
        setLinesError(null);
        /*
         * Any line that has become billed since the selection was made drops
         * out of it. Re-reading after a refusal is how the "already invoiced"
         * 422 stops being repeatable — leaving a now-billed id ticked would
         * send the same refused request again.
         */
        const billable = new Set(
          response.data.filter((line) => !line.invoiced).map((line) => line.id),
        );
        setSelected((current) => new Set([...current].filter((id) => billable.has(id))));
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setLines(null);
        setLinesError(caught instanceof Error ? caught.message : 'Failed to load the line items');
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLinesLoading(false);
      });
    return () => controller.abort();
  }, [open, quoteId, reloadToken]);

  const toggle = useCallback((lineId: number) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  }, []);

  const billable = (lines ?? []).filter((line) => !line.invoiced);
  const alreadyBilled = (lines ?? []).filter((line) => line.invoiced);
  /*
   * ⚠️ Built from the RENDERED order, and the same array is sent. An indexed
   * refusal (`quoteProductIds.4`) is only mappable back to a row because the
   * order the ids went out in is the order they are held in here.
   */
  const sentIds = billable.filter((line) => selected.has(line.id)).map((line) => line.id);
  const selectedTotal = sumDecimalStrings(
    billable.filter((line) => selected.has(line.id)).map((line) => line.lineTotal),
  );

  const trimmedSubject = subject.trim();

  /*
   * Client-side refusals, each mirroring one the server would give — so a
   * mistake is named on the control rather than as a round trip. The server
   * still runs all of them; these are the optimisation, the 422 branch above is
   * the guarantee.
   *
   * ⚠️ The date ordering check fires ONLY when both are present, exactly as the
   * server's `.refine` does, and reports on `dueDate` because that is the path
   * the server puts it on. A `YYYY-MM-DD` string compare is the calendar
   * compare here — never build a `Date` from one of these.
   */
  const localFields: Record<string, string> = {};
  if (trimmedSubject.length > MAX_SUBJECT) {
    localFields.subject = `Keep this to ${MAX_SUBJECT} characters — this is ${trimmedSubject.length}.`;
  }
  if (billDate !== '' && !isRealDate(billDate)) {
    localFields.billDate = 'That isn’t a real date.';
  }
  if (dueDate !== '' && !isRealDate(dueDate)) {
    localFields.dueDate = 'That isn’t a real date.';
  }
  if (
    billDate !== '' &&
    dueDate !== '' &&
    isRealDate(billDate) &&
    isRealDate(dueDate) &&
    billDate > dueDate
  ) {
    localFields.dueDate = 'The bill date must be on or before the due date.';
  }

  const tooMany = sentIds.length > MAX_LINES;
  const blocked =
    sentIds.length === 0 || tooMany || Object.keys(localFields).length > 0 || linesLoading;

  /* Local checks win over the server's, so a stale refusal cannot mask a live one. */
  const shownFields = { ...refusal.fields, ...localFields };

  async function submit() {
    if (blocked || busy) return;
    setBusy(true);
    setRefusal(EMPTY_REFUSAL);

    const body: QuotePartialInvoiceInput = { quoteProductIds: sentIds };
    /*
     * ⚠️ Each optional key is OMITTED rather than sent empty. `subject` is
     * `.optional()` and NOT `.nullish()`, so `null` is a 422 — which inverts
     * this app's usual "clearing sends null" rule — and an empty string fails
     * the 1..255 minimum. Omitting is what asks for the server's default.
     */
    if (trimmedSubject !== '') body.subject = trimmedSubject;
    if (billDate !== '') body.billDate = billDate;
    if (dueDate !== '') body.dueDate = dueDate;

    try {
      const response = await api.raiseInvoiceForQuoteLines(quoteId, body);
      /*
       * ⚠️ **`xeroPush` DESCRIBES THE INVOICE, not the quote this was called on.**
       * The controller pushes the document it just made, so `'invoices'` is the
       * entity — naming the quote would send a reader to correct the wrong record.
       *
       * ⚠️ **`null` is the ordinary answer and means carry on.** A `pushed` needs
       * no words and a `skipped` — no line items, or a company nobody has linked —
       * is the commonest outcome there is; the invoice's own Xero panel states
       * both, one navigation away. Only a refusal is held, because only a refusal
       * is invisible everywhere else.
       */
      const report = conversionPushReport('invoices', response.data.subject, response.xeroPush);
      if (report === null) {
        onRaised(response.data);
        return;
      }
      setRaised({ invoice: response.data, report });
    } catch (caught) {
      setRefusal(refusalOf(caught, sentIds));
      /*
       * Re-read the basket on every refusal. The most likely one is "already
       * invoiced", which means the flags on screen are stale in exactly the
       * direction that would send the identical request again.
       */
      reload();
    } finally {
      setBusy(false);
    }
  }

  /*
   * ⚠️ **HANDS THE INVOICE OVER, and this is the ONLY way out of the held state.**
   * Every exit — the footer button, Escape, the close control — resolves to this,
   * because the invoice EXISTS and `quote_id` carries no UNIQUE constraint, so a
   * person who walks away without being taken to it is the person who bills the
   * same lines again. It reads as "open" rather than "dismiss" for the same
   * reason: nothing here is undone by closing.
   */
  function openRaised() {
    if (raised === null) return;
    onRaised(raised.invoice);
  }

  if (raised !== null) {
    return (
      <Modal
        open={open}
        /*
         * ⚠️ Not `onClose`. Closing this dialog is not abandoning the request —
         * the invoice was created — so the close path goes where the button goes.
         */
        onClose={openRaised}
        /*
         * ⚠️ The title states the outcome rather than the action. "Bill selected
         * line items" over a report about an invoice that already exists reads as
         * an offer to do it again, which is the one thing that must not happen.
         */
        title={`Invoice #${raised.invoice.id} was raised`}
        size="lg"
        dismissOnBackdrop={false}
        footer={
          <Button variant="primary" onClick={openRaised}>
            Open invoice #{raised.invoice.id}
          </Button>
        }
      >
        <div className="space-y-4">
          {/*
            ⚠️ **THE INVOICE EXISTS, AND THE HEADING SAYS SO FIRST.** The raise
            landed and the Xero half did not, and there is no rollback and no
            compensating endpoint safe to fire on anybody's behalf — so the only
            truthful report names what stands. "Nothing was raised" here is the
            sentence that produces a second invoice against the same lines.
          */}
          <div
            role="alert"
            className="rounded-md border border-warning-border bg-warning-soft px-3 py-2 text-xs break-words text-warning"
          >
            <p className="m-0">
              <strong className="font-semibold">
                Invoice #{raised.invoice.id} was raised for{' '}
                {pluralise(sentIds.length, 'line item')}.
              </strong>{' '}
              Billing these lines again would raise a second invoice for them.
            </p>
          </div>

          {/*
            Every string below is `rowPushReport`'s or the SERVER's own — only the
            heading is replaced, by `conversionPushReport`, and it leads with the
            invoice having been created. No dismiss: dismissing the one report of
            this anywhere is not an outcome worth offering, and the footer already
            has the way out.
          */}
          <div className="overflow-hidden rounded-md border border-border">
            <XeroPushReportBanner report={raised.report} onDismiss={openRaised} />
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={busy ? () => undefined : onClose}
      title="Bill selected line items"
      size="lg"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={busy}
            disabled={blocked}
            {...(sentIds.length === 0 && !linesLoading
              ? { title: 'Tick at least one line item to bill.' }
              : {})}
          >
            {sentIds.length === 0
              ? 'Raise invoice'
              : `Raise invoice for ${pluralise(sentIds.length, 'line')}`}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/*
          ⚠️ **THE STANDING NOTE WAS REMOVED ON THE OWNER'S INSTRUCTION,
          2026-08-30, and this comment is what is left of it** — so the next
          person does not read the gap as an oversight and write it back.

          It said two things: that billing lines does NOT accept the quote (the
          stage is set from the quote form or by moving the card on the board),
          and that a quote may be billed across several invoices because
          `quote_id` carries no UNIQUE constraint.

          **Both are still TRUE of the behaviour — only the sentence is gone.**
          Two consequences worth keeping in view rather than rediscovering:

          - Somebody who bills every line here will expect the quote to read
            Accepted. It will not, and nothing on this screen now says so; if
            that turns up as "the call half-failed", this is why.
          - Nothing anywhere may be worded as "the invoice for this quote".
            That constraint is on the DATA, not on this notice, and it outlives
            it — the panel behind this dialog says "invoices raised from this
            quote" for the same reason.
        */}

        {linesError !== null && (
          <div
            role="alert"
            className="flex flex-wrap items-start gap-x-2 gap-y-1 rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-xs text-danger"
          >
            <span className="font-semibold">Couldn&rsquo;t load the line items.</span>
            <span className="min-w-0 [overflow-wrap:anywhere]">{linesError}</span>
            <Button variant="secondary" size="sm" onClick={reload}>
              Try again
            </Button>
          </div>
        )}

        {refusal.banner.length > 0 && (
          <div
            role="alert"
            className="rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-xs text-danger"
          >
            <p className="m-0 font-semibold">The invoice wasn&rsquo;t raised.</p>
            {refusal.banner.map((line) => (
              <p key={line} className="m-0 mt-1 [overflow-wrap:anywhere]">
                {line}
              </p>
            ))}
          </div>
        )}

        {linesLoading ? (
          <div className="grid gap-2" aria-busy="true">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : lines === null ? null : lines.length === 0 ? (
          <p className="m-0 text-xs text-fg-muted">
            Nothing to bill — add line items to this quote first.
          </p>
        ) : (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="m-0 text-xs text-fg-muted">
                {billable.length === 0
                  ? `Every line on this quote has been billed — ${pluralise(alreadyBilled.length, 'line')} in total.`
                  : `${pluralise(billable.length, 'line')} still to bill${
                      alreadyBilled.length > 0 ? `, ${alreadyBilled.length} already billed` : ''
                    }.`}
              </p>
              {billable.length > 0 && (
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy || sentIds.length === billable.length}
                    onClick={() => setSelected(new Set(billable.map((line) => line.id)))}
                  >
                    Select all
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy || sentIds.length === 0}
                    onClick={() => setSelected(new Set())}
                  >
                    Clear
                  </Button>
                </div>
              )}
            </div>

            <TableWrap frozenHeader frozenMaxHeight="min(55vh, 480px)">
              <Table className="min-w-[560px]">
                <THead>
                  <TR>
                    <TH className="w-10">
                      <span className="sr-only">Bill</span>
                    </TH>
                    <TH>Line item</TH>
                    <TH numeric className="w-24">
                      Qty
                    </TH>
                    <TH numeric className="w-40">
                      Line total
                    </TH>
                  </TR>
                </THead>
                <TBody>
                  {lines.map((line) => {
                    const lineError = refusal.lines[line.id];
                    return (
                      <TR key={line.id}>
                        <TD>
                          {/*
                            A billed line is shown DISABLED rather than hidden.
                            Removing the row would make a quote that is half
                            billed look like a quote with half as many lines,
                            and the invoice that took it is the thing somebody
                            reconciling this actually came for.
                          */}
                          <Checkbox
                            label={<span className="sr-only">Bill {line.product.productName}</span>}
                            checked={selected.has(line.id)}
                            disabled={line.invoiced || busy}
                            onChange={() => toggle(line.id)}
                          />
                        </TD>
                        <TH scope="row">
                          <span className="[overflow-wrap:anywhere]">
                            {line.product.productName}
                          </span>
                          {line.invoiced && (
                            <span className="mt-0.5 block text-xs text-fg-muted [overflow-wrap:anywhere]">
                              {line.invoicedOn === null ? (
                                /*
                                  `invoiced` is exactly `invoicedOn !== null`
                                  server-side, so this branch should be
                                  unreachable — it says what it knows rather
                                  than naming an invoice it was not given.
                                */
                                'Already billed'
                              ) : (
                                <>
                                  Billed on{' '}
                                  <Link
                                    to={`/invoices/${line.invoicedOn.id}`}
                                    className="rounded-sm text-accent-brand transition-colors duration-150 ease-out hover:underline motion-reduce:transition-none"
                                  >
                                    {line.invoicedOn.subject}
                                  </Link>
                                </>
                              )}
                            </span>
                          )}
                          {lineError !== undefined && (
                            <span
                              role="alert"
                              className="mt-0.5 block text-xs text-danger [overflow-wrap:anywhere]"
                            >
                              {lineError}
                            </span>
                          )}
                        </TH>
                        <TD numeric>{line.quantity}</TD>
                        <TD numeric>
                          {line.lineTotal === null ? (
                            <span className="text-fg-subtle">—</span>
                          ) : (
                            <span className="tabular">
                              {formatDecimalString(line.lineTotal)}
                              {quote.currency ? ` ${quote.currency}` : ''}
                            </span>
                          )}
                        </TD>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            </TableWrap>

            {tooMany && (
              <p role="alert" className="m-0 text-xs text-danger">
                {sentIds.length} ticked — {MAX_LINES} is the most for one invoice. Bill them
                across two.
              </p>
            )}

            {selectedTotal !== null && sentIds.length > 0 && (
              /*
                ⚠️ Labelled as the SELECTED LINES' total, never as the
                invoice's. The server recomputes the new invoice's four header
                totals from the copied lines — unlike `convert`, which copies
                the quote's own — so this is the sum of the server's own
                `lineTotal` values and nothing more. Summing those rather than
                recomputing is the house rule: the database rounds per line and
                its answer is what Xero reads.
              */
              <p className="m-0 text-xs text-fg-secondary">
                Selected lines come to{' '}
                <span className="tabular font-semibold text-fg">
                  {formatDecimalString(selectedTotal)}
                  {quote.currency ? ` ${quote.currency}` : ''}
                </span>
                .
              </p>
            )}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-3">
          <Field
            label="Subject"
            {...(shownFields.subject === undefined ? {} : { error: shownFields.subject })}
          >
            {(props) => (
              <Input
                {...props}
                value={subject}
                maxLength={MAX_SUBJECT}
                disabled={busy}
                invalid={shownFields.subject !== undefined}
                onChange={(event) => setSubject(event.target.value)}
              />
            )}
          </Field>

          <Field
            label="Bill date"
            {...(shownFields.billDate === undefined ? {} : { error: shownFields.billDate })}
          >
            {(props) => (
              <Input
                {...props}
                type="date"
                value={billDate}
                disabled={busy}
                invalid={shownFields.billDate !== undefined}
                onChange={(event) => setBillDate(event.target.value)}
              />
            )}
          </Field>

          <Field
            label="Due date"
            {...(shownFields.dueDate === undefined ? {} : { error: shownFields.dueDate })}
          >
            {(props) => (
              <Input
                {...props}
                type="date"
                value={dueDate}
                disabled={busy}
                invalid={shownFields.dueDate !== undefined}
                onChange={(event) => setDueDate(event.target.value)}
              />
            )}
          </Field>
        </div>
      </div>
    </Modal>
  );
}
